import React from 'react';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

const st = {
  page: {
    width: '210mm',
    minHeight: '297mm',
    padding: '12mm 12mm',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    fontSize: '10px',
    color: '#1a1a1a',
    lineHeight: '1.35',
    background: '#fff',
    boxSizing: 'border-box',
    pageBreakAfter: 'always',
  },
  pageLast: { pageBreakAfter: 'auto' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '3px solid #0f172a',
    paddingBottom: '8px',
    marginBottom: '10px',
  },
  companyName: { fontSize: '22px', fontWeight: 800, color: '#0f172a', letterSpacing: '2px' },
  companySubtitle: { fontSize: '9px', color: '#64748b', marginTop: '2px' },
  docTitle: { fontSize: '13px', fontWeight: 800, color: '#0f172a', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '1px' },
  docNumber: { fontSize: '18px', fontWeight: 800, fontFamily: 'monospace', textAlign: 'right', marginTop: '2px', color: '#0f172a' },
  docDate: { fontSize: '8px', color: '#64748b', textAlign: 'right', marginTop: '4px' },

  sectionTitle: {
    fontSize: '10px', fontWeight: 700, color: '#fff', textTransform: 'uppercase',
    letterSpacing: '1px', background: '#0f172a', padding: '5px 9px',
    marginBottom: '6px', marginTop: '12px',
  },

  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '5px 9px' },
  infoBox: { border: '1px solid #e2e8f0', padding: '4px 6px', borderRadius: '2px' },
  lbl: { fontSize: '7px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' },
  val: { fontSize: '10px', fontWeight: 600, color: '#1e293b' },
  valMono: { fontSize: '11px', fontWeight: 700, color: '#0f172a', fontFamily: 'monospace' },

  // Phase navigation
  navWrap: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 1fr',
    gap: '6px',
    marginTop: '8px',
  },
  navCell: {
    border: '1.5px solid #cbd5e1',
    borderRadius: '4px',
    padding: '8px 10px',
    background: '#f8fafc',
    minHeight: '52px',
  },
  navCellCurrent: {
    border: '3px solid #f59e0b',
    borderRadius: '6px',
    padding: '10px 12px',
    background: '#fffbeb',
    boxShadow: '0 0 0 1px rgba(245,158,11,0.2) inset',
    minHeight: '52px',
  },
  navLbl: { fontSize: '8px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '3px' },
  navLblCurrent: { fontSize: '9px', fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '3px' },
  navNumber: { fontSize: '18px', fontWeight: 800, color: '#475569', fontFamily: 'monospace' },
  navNumberCurrent: { fontSize: '26px', fontWeight: 900, color: '#92400e', fontFamily: 'monospace' },
  navDesc: { fontSize: '10px', fontWeight: 600, color: '#334155', marginTop: '2px' },
  navDescCurrent: { fontSize: '12px', fontWeight: 700, color: '#1e293b', marginTop: '3px' },

  // Phase detail
  detailBlock: {
    border: '2px solid #f59e0b',
    borderRadius: '6px',
    padding: '12px',
    marginTop: '10px',
    background: '#fffaf0',
  },
  detailRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px 12px', marginTop: '6px' },
  notesBox: {
    border: '1.5px dashed #94a3b8', borderRadius: '3px', padding: '6px 8px',
    marginTop: '6px', minHeight: '28px', background: '#fff', fontSize: '9px',
  },
  chip: (bg, color) => ({
    display: 'inline-block', padding: '2px 7px', borderRadius: '8px',
    fontSize: '8px', fontWeight: 700, background: bg, color, marginRight: '4px',
  }),

  // Withdrawal table
  wTable: { width: '100%', borderCollapse: 'collapse', marginTop: '4px' },
  wTh: {
    background: '#334155', color: '#fff', padding: '6px 8px', textAlign: 'left',
    fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
    border: '1px solid #1e293b',
  },
  wTd: { padding: '0', borderBottom: '1px solid #94a3b8', borderRight: '1px solid #cbd5e1', borderLeft: '1px solid #cbd5e1', height: '18px' },

  footer: {
    position: 'absolute',
    bottom: '10mm',
    left: '12mm',
    right: '12mm',
    paddingTop: '4px',
    borderTop: '1px solid #cbd5e1',
    display: 'flex', justifyContent: 'space-between',
    fontSize: '7.5px', color: '#94a3b8',
  },
  pageBadge: {
    display: 'inline-block', padding: '3px 10px',
    background: '#0f172a', color: '#fff', fontWeight: 800,
    fontSize: '9px', borderRadius: '12px', letterSpacing: '0.5px',
  },
};


const PhasePage = ({ data, phase, prevPhase, nextPhase, pageIndex, totalPages, isLast }) => {
  const { lot, product, client, revision, order, order_line, control_plans = [], revision_tools = [] } = data;

  const phaseControls = (control_plans || []).filter(cp => cp.work_phase_id === phase?.id);
  const phaseTools = (revision_tools || []).filter(rt => rt.work_phase_id === phase?.id);

  const pageStyle = { ...st.page, ...(isLast ? st.pageLast : {}), position: 'relative' };

  return (
    <div style={pageStyle}>
      {/* HEADER */}
      <div style={st.header}>
        <div>
          <div style={st.companyName}>EVOTECH</div>
          <div style={st.companySubtitle}>Lavorazioni Meccaniche di Precisione</div>
        </div>
        <div>
          <div style={st.docTitle}>Foglio di Fase</div>
          <div style={st.docNumber}>{lot.lot_code}</div>
          <div style={st.docDate}>
            <span style={st.pageBadge}>Fase {pageIndex + 1} di {totalPages}</span>
            <span style={{ marginLeft: '6px' }}>Stampato il {fmtDate(new Date())}</span>
          </div>
        </div>
      </div>

      {/* COMMESSA INFO */}
      <div style={st.infoGrid}>
        <div style={st.infoBox}><div style={st.lbl}>Cliente</div><div style={st.val}>{client?.name || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>N. Ordine</div><div style={st.valMono}>{order?.order_number || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Rif. Cliente</div><div style={st.val}>{order?.customer_order_ref || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Consegna Richiesta</div><div style={{ ...st.val, color: '#dc2626', fontWeight: 700 }}>{order_line?.delivery_date_required || order?.required_delivery_date || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Codice Prodotto</div><div style={st.valMono}>{product?.code || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Descrizione</div><div style={st.val}>{product?.name || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Revisione</div><div style={st.valMono}>{revision?.revision_code || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Lotto Cliente</div><div style={{ ...st.valMono, fontWeight: 700 }}>{lot?.customer_lot_snapshot || order_line?.customer_lot || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Configurazione</div><div style={st.valMono}>{lot?.configuration_code || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Materiale</div><div style={st.val}>{product?.material || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Quantita Ordinata</div><div style={{ ...st.val, fontWeight: 800 }}>{lot?.quantity || 0} pz</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Data Apertura</div><div style={st.val}>{lot?.opening_date || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Lotto</div><div style={st.valMono}>{lot?.lot_code}</div></div>
      </div>

      {/* PHASE NAVIGATION */}
      <div style={st.sectionTitle}>Sequenza Ciclo di Lavoro</div>
      <div style={st.navWrap}>
        {/* PREV */}
        <div style={st.navCell}>
          <div style={st.navLbl}>Fase Precedente</div>
          {prevPhase ? (
            <>
              <div style={st.navNumber}>{prevPhase.phase_number}</div>
              <div style={st.navDesc}>{prevPhase.description}</div>
            </>
          ) : (
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', marginTop: '4px' }}>Inizio ciclo</div>
          )}
        </div>
        {/* CURRENT */}
        <div style={st.navCellCurrent}>
          <div style={st.navLblCurrent}>Fase Corrente</div>
          <div style={st.navNumberCurrent}>{phase.phase_number}</div>
          <div style={st.navDescCurrent}>{phase.description}</div>
        </div>
        {/* NEXT */}
        <div style={st.navCell}>
          <div style={st.navLbl}>Fase Successiva</div>
          {nextPhase ? (
            <>
              <div style={st.navNumber}>{nextPhase.phase_number}</div>
              <div style={st.navDesc}>{nextPhase.description}</div>
            </>
          ) : (
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#ef4444', marginTop: '4px' }}>Fine ciclo</div>
          )}
        </div>
      </div>

      {/* CURRENT PHASE DETAIL */}
      <div style={st.detailBlock}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <span style={{ fontSize: '11px', color: '#92400e', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dettaglio Operativo</span>
          </div>
          <div>
            <span style={st.chip('#fde68a', '#92400e')}>Fase {phase.phase_number}</span>
          </div>
        </div>
        <div style={st.detailRow}>
          <div><div style={st.lbl}>Reparto</div><div style={st.val}>{phase.department || '-'}</div></div>
          <div><div style={st.lbl}>Macchina</div><div style={st.valMono}>{phase.default_machine || '-'}</div></div>
          <div><div style={st.lbl}>Attrezzatura</div><div style={st.val}>{phase.equipment || '-'}</div></div>
          <div><div style={st.lbl}>Tempo Standard</div><div style={st.val}>{phase.standard_time_minutes ? `${phase.standard_time_minutes} min` : '-'}</div></div>
          <div><div style={st.lbl}>Tempo Setup</div><div style={st.val}>{phase.setup_time_minutes ? `${phase.setup_time_minutes} min` : '-'}</div></div>
          <div><div style={st.lbl}>Codice Fase</div><div style={st.valMono}>{phase.phase_code || phase.code || '-'}</div></div>
        </div>
        <div style={{ marginTop: '8px' }}>
          <div style={st.lbl}>Note di Fase / Istruzioni</div>
          <div style={st.notesBox}>{phase.notes || phase.work_instructions || ''}</div>
        </div>

        {/* Controls */}
        {phaseControls.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <div style={st.lbl}>Controlli Qualita Collegati</div>
            <div style={{ marginTop: '4px' }}>
              {phaseControls.map(c => (
                <span key={c.id} style={st.chip('#dbeafe', '#1e3a8a')}>
                  {c.control_code} - {c.characteristic}
                  {c.nominal_value ? ` (${c.nominal_value}${c.unit_of_measure || ''})` : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tools */}
        {phaseTools.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <div style={st.lbl}>Utensili Collegati</div>
            <div style={{ marginTop: '4px' }}>
              {phaseTools.map(t => (
                <span key={t.id} style={st.chip(t.critical ? '#fecaca' : '#e0e7ff', t.critical ? '#991b1b' : '#3730a3')}>
                  {t.tool_code} - {t.tool_description || ''}{t.critical ? ' (CRITICO)' : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* WITHDRAWAL TABLE */}
      <div style={st.sectionTitle}>Registro Prelievi / Avanzamento</div>
      <table style={st.wTable}>
        <thead>
          <tr>
            <th style={{ ...st.wTh, width: '18%' }}>Data</th>
            <th style={{ ...st.wTh, width: '28%' }}>Prelievo</th>
            <th style={{ ...st.wTh, width: '22%', textAlign: 'center' }}>Quantita Prelievo</th>
            <th style={{ ...st.wTh, width: '32%' }}>Responsabile</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 15 }).map((_, i) => (
            <tr key={i}>
              <td style={st.wTd}></td>
              <td style={st.wTd}></td>
              <td style={st.wTd}></td>
              <td style={st.wTd}></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* FOOTER */}
      <div style={st.footer}>
        <span>Internal ERP - Evotech SRL | Foglio di fase - Uso interno</span>
        <span>{lot.lot_code} · Pagina {pageIndex + 1} / {totalPages}</span>
      </div>
    </div>
  );
};


const SimplifiedPage = ({ data }) => {
  const { lot, product, client, order, order_line } = data;
  return (
    <div style={{ ...st.page, ...st.pageLast, position: 'relative' }}>
      <div style={st.header}>
        <div>
          <div style={st.companyName}>EVOTECH</div>
          <div style={st.companySubtitle}>Lavorazioni Meccaniche di Precisione</div>
        </div>
        <div>
          <div style={st.docTitle}>Foglio Commessa Semplificato</div>
          <div style={st.docNumber}>{lot.lot_code}</div>
          <div style={st.docDate}>
            <span style={{ ...st.pageBadge, background: '#b45309' }}>Avanzamento semplificato</span>
            <span style={{ marginLeft: '6px' }}>Stampato il {fmtDate(new Date())}</span>
          </div>
        </div>
      </div>

      <div style={st.infoGrid}>
        <div style={st.infoBox}><div style={st.lbl}>Cliente</div><div style={st.val}>{client?.name || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>N. Ordine</div><div style={st.valMono}>{order?.order_number || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Rif. Cliente</div><div style={st.val}>{order?.customer_order_ref || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Consegna Richiesta</div><div style={{ ...st.val, color: '#dc2626', fontWeight: 700 }}>{order_line?.delivery_date_required || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Codice Prodotto</div><div style={st.valMono}>{product?.code || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Descrizione</div><div style={st.val}>{product?.name || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Materiale</div><div style={st.val}>{product?.material || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Lotto Cliente</div><div style={{ ...st.valMono, fontWeight: 700 }}>{lot?.customer_lot_snapshot || order_line?.customer_lot || '-'}</div></div>
        <div style={st.infoBox}><div style={st.lbl}>Quantita Ordinata</div><div style={{ ...st.val, fontWeight: 800 }}>{lot?.quantity || 0} pz</div></div>
      </div>

      <div style={{ ...st.detailBlock, borderColor: '#cbd5e1', background: '#f8fafc', marginTop: '14px' }}>
        <div style={{ fontSize: '11px', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Nessun flusso prodotto configurato
        </div>
        <p style={{ fontSize: '10px', color: '#475569', marginTop: '6px', lineHeight: 1.5 }}>
          Questo prodotto non ha un ciclo di lavoro definito. Utilizzare il registro prelievi sottostante per la
          tracciatura manuale dei materiali e dell'avanzamento di produzione.
        </p>
      </div>

      <div style={st.sectionTitle}>Registro Prelievi / Avanzamento</div>
      <table style={st.wTable}>
        <thead>
          <tr>
            <th style={{ ...st.wTh, width: '18%' }}>Data</th>
            <th style={{ ...st.wTh, width: '28%' }}>Prelievo</th>
            <th style={{ ...st.wTh, width: '22%', textAlign: 'center' }}>Quantita Prelievo</th>
            <th style={{ ...st.wTh, width: '32%' }}>Responsabile</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 15 }).map((_, i) => (
            <tr key={i}>
              <td style={{ ...st.wTd, height: '22px' }}></td>
              <td style={{ ...st.wTd, height: '22px' }}></td>
              <td style={{ ...st.wTd, height: '22px' }}></td>
              <td style={{ ...st.wTd, height: '22px' }}></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={st.footer}>
        <span>Internal ERP - Evotech SRL | Foglio commessa semplificato</span>
        <span>{lot.lot_code} · Pagina 1 / 1</span>
      </div>
    </div>
  );
};


const PrintablePhaseSheets = React.forwardRef(({ data }, ref) => {
  if (!data) return <div ref={ref}></div>;

  const phases = (data.work_cycles?.[0]?.phases || []).slice().sort((a, b) => (a.phase_number || 0) - (b.phase_number || 0));

  return (
    <div ref={ref}>
      <style>{'@page { size: A4 portrait; margin: 8mm; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }'}</style>
      {phases.length === 0 ? (
        <SimplifiedPage data={data} />
      ) : phases.map((phase, idx) => (
        <PhasePage
          key={phase.id || idx}
          data={data}
          phase={phase}
          prevPhase={idx > 0 ? phases[idx - 1] : null}
          nextPhase={idx < phases.length - 1 ? phases[idx + 1] : null}
          pageIndex={idx}
          totalPages={phases.length}
          isLast={idx === phases.length - 1}
        />
      ))}
    </div>
  );
});

PrintablePhaseSheets.displayName = 'PrintablePhaseSheets';
export default PrintablePhaseSheets;
